/**
 * This custom hook provides the functionality to save a user with the given parameters.
 */

import React, {useEffect, useState} from "react";

const useGeneralEditUserForm = (selectedUser, editedUser) => {

    // This state is only necessary for "new user"
    const [email, setEmail] = useState(selectedUser?.email || '')
    const [password, setPassword] = useState(selectedUser?.password || '')
    const [passwordRepeat, setPasswordRepeat] = useState(selectedUser?.passwordRepeat || '')

    // Editable values (user values) - used to control the controlled elements' states
    const [firstName, setFirstName] = React.useState(selectedUser?.firstName || '');
    const [secondName, setSecondName] = React.useState(selectedUser?.secondName || '');
    const [role, setRole] = React.useState(selectedUser?.role || '')
    const [roleScopes, setRoleScopes] = React.useState(selectedUser?.roleScopes || [])
    const [associationInfo, setAssociationInfo] = useState(selectedUser?.associationInfo || {})

    // Whether or not the user is able to approve/reject scored scenes
    const [isReviewer, setIsReviewer] = useState(selectedUser?.isReviewer || false)

    // If the some relevant state changes, also the reference needs to be updated
    useEffect(() => {
        editedUser.current = {
            ...editedUser.current,
            uid: selectedUser?.uid,
            email: email,
            firstName: firstName,
            secondName: secondName,
            associationInfo: associationInfo,
            role: role,
            roleScopes: roleScopes,
            password: password,
            passwordRepeat: passwordRepeat,
            isReviewer: isReviewer
        }
    }, [email, firstName, secondName, associationInfo, role, roleScopes, password, passwordRepeat, isReviewer])

    /*
    Functions for controlling the state of the controlled input elements.
    The state is updated on each value change / key stroke, this allows a flexible and dynamic handling.
     */
    const handleChangeFirstName = (event) => {
        setFirstName(event.target.value)
    }

    const handleChangeSecondName = (event) => {
        setSecondName(event.target.value)
    }

    const handleChangeEmail = (event) => {
        setEmail(event.target.value)
    }

    const handleChangeDisciplines = (value, scope) => {
        const newInfo = {
            ...associationInfo,
            [scope]: {
                disciplines: value,
                experience: associationInfo[scope]?.experience || '',
            }
        }

        setAssociationInfo(newInfo)
    }

    const handleChangeExperience = (event, scope) => {
        const newInfo = {
            ...associationInfo,
            [scope]: {
                disciplines: associationInfo[scope]?.disciplines || [],
                experience: event.target.value
            }
        }

        setAssociationInfo(newInfo)
    }

    const handleRoleChange = (event) => {
        setRole(event.target.value)
    }

    const handleChangeRoleScopes = (event, value) => {
        setRoleScopes(value)

        const newInfo = {...associationInfo}

        // Delete information from the user association info in case the corresponding scope is deleted
        Object.keys(associationInfo).forEach((scope) => {
            if (!value.includes(scope)) {
                delete newInfo[scope]
            }
        })

        // Initialized newly added scopes with default values to make validity checking easier
        value.forEach((scope) => {
            if (!associationInfo[scope]) {
                newInfo[scope] = {
                    disciplines: [],
                    experience: ''
                }
            }
        })

        setAssociationInfo(newInfo)
    }

    const handleChangePassword = (event) => {
        setPassword(event.target.value)
    }

    const handleChangePasswordRepeat = (event) => {
        setPasswordRepeat(event.target.value)
    }

    const handleChangeIsReviewer = (event) => {
        setIsReviewer(event.target.checked)
    }

    return {
        email,
        firstName,
        secondName,
        associationInfo,
        role,
        roleScopes,
        password,
        passwordRepeat,
        isReviewer,
        handleChangeFirstName,
        handleChangeSecondName,
        handleChangeEmail,
        handleChangeDisciplines,
        handleChangeExperience,
        handleRoleChange,
        handleChangeRoleScopes,
        handleChangePassword,
        handleChangePasswordRepeat,
        handleChangeIsReviewer
    }
}
export default useGeneralEditUserForm;